/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {AnimationAnimateMetadata, AnimationGroupMetadata, AnimationKeyframesSequenceMetadata, AnimationMetadata, AnimationSequenceMetadata, AnimationStateMetadata, AnimationStyleMetadata, AnimationTransitionMetadata, ɵStyleData} from '@angular/animations';

import {copyStyles, normalizeStyles} from '../util';

import {AnimationDslVisitor, visitAnimationNode} from './animation_dsl_visitor';
import {parseTransitionExpr} from './animation_transition_expr';
import {AnimationTransitionFactory} from './animation_transition_factory';
import {AnimationTransitionInstruction, createTransitionInstruction} from './animation_transition_instruction';
import {validateAnimationSequence} from './animation_validator_visitor';
/**
 * \@experimental Animation support is experimental.
 * @param {?} name
 * @param {?} definitions
 * @return {?}
 */
export function buildTrigger(name: string, definitions: AnimationMetadata[]): AnimationTrigger {
  return new AnimationTriggerVisitor().buildTrigger(name, definitions);
}
/**
 * \@experimental Animation support is experimental.
 */
export class AnimationTrigger {
public transitionFactories: AnimationTransitionFactory[] = [];
public states: {[stateName: string]: ɵStyleData} = {};
/**
 * @param {?} name
 * @param {?} states
 * @param {?} _transitionAsts
 */
constructor(
public name: string, states: {[stateName: string]: ɵStyleData},
private _transitionAsts: AnimationTransitionMetadata[]) {
    Object.keys(states).forEach(
        stateName => { this.states[stateName] = copyStyles(states[stateName], false); });

    const errors: string[] = [];
    _transitionAsts.forEach(ast => {
      const exprs = parseTransitionExpr(ast.expr, errors);
      const sequenceErrors = validateAnimationSequence(ast);
      if (sequenceErrors.length) {
        errors.push(...sequenceErrors);
      } else {
        this.transitionFactories.push(
            new AnimationTransitionFactory(this.name, ast, exprs, states));
      }
    });

    if (errors.length) {
      const LINE_START = '\n - ';
      throw new Error(
          `Animation parsing for the ${name} trigger have failed:${LINE_START}${errors.join(LINE_START)}`);
    }
  }
/**
 * @param {?} currentState
 * @param {?} nextState
 * @return {?}
 */
createFallbackInstruction(currentState: any, nextState: any): AnimationTransitionInstruction {
    const /** @type {?} */ backupStateStyles = this.states['*'] || {};
    const /** @type {?} */ currentStateStyles = this.states[currentState] || backupStateStyles;
    const /** @type {?} */ nextStateStyles = this.states[nextState] || backupStateStyles;
    return createTransitionInstruction(
        this.name, currentState, nextState, nextState == 'void', currentStateStyles,
        nextStateStyles, []);
  }
/**
 * @param {?} currentState
 * @param {?} nextState
 * @return {?}
 */
matchTransition(currentState: any, nextState: any): AnimationTransitionInstruction|null {
    for (let /** @type {?} */ i = 0; i < this.transitionFactories.length; i++) {
      let /** @type {?} */ result = this.transitionFactories[i].match(currentState, nextState);
      if (result) return result;
    }
    return null;
  }
}

function AnimationTrigger_tsickle_Closure_declarations() {
/** @type {?} */
AnimationTrigger.prototype.transitionFactories;
/** @type {?} */
AnimationTrigger.prototype.states;
/** @type {?} */
AnimationTrigger.prototype.name;
/** @type {?} */
AnimationTrigger.prototype._transitionAsts;
}

class AnimationTriggerContext {
public errors: string[] = [];
public states: {[stateName: string]: ɵStyleData} = {};
public transitions: AnimationTransitionMetadata[] = [];
}

function AnimationTriggerContext_tsickle_Closure_declarations() {
/** @type {?} */
AnimationTriggerContext.prototype.errors;
/** @type {?} */
AnimationTriggerContext.prototype.states;
/** @type {?} */
AnimationTriggerContext.prototype.transitions;
}

class AnimationTriggerVisitor implements AnimationDslVisitor {
/**
 * @param {?} name
 * @param {?} definitions
 * @return {?}
 */
buildTrigger(name: string, definitions: AnimationMetadata[]): AnimationTrigger {
    const /** @type {?} */ context = new AnimationTriggerContext();
    definitions.forEach(def => visitAnimationNode(this, def, context));
    return new AnimationTrigger(name, context.states, context.transitions);
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitState(ast: AnimationStateMetadata, context: any): any {
    const /** @type {?} */ styles = normalizeStyles(ast.styles.styles);
    ast.name.split(/\s*,\s*/).forEach(name => { context.states[name] = styles; });
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitTransition(ast: AnimationTransitionMetadata, context: any): any {
    context.transitions.push(ast);
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitSequence(ast: AnimationSequenceMetadata, context: any) {
    // these values are not visited in this AST
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitGroup(ast: AnimationGroupMetadata, context: any) {
    // these values are not visited in this AST
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitAnimate(ast: AnimationAnimateMetadata, context: any) {
    // these values are not visited in this AST
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitStyle(ast: AnimationStyleMetadata, context: any) {
    // these values are not visited in this AST
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitKeyframeSequence(ast: AnimationKeyframesSequenceMetadata, context: any) {
    // these values are not visited in this AST
  }
}
